import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { usePermissions } from '@hooks/usePermissions';
import { useDragScroll } from '@/hooks/useDragScroll';
import { getConfig } from '../config';
import { 
  LayoutDashboard, 
  Package, 
  Truck,
  Radio, 
  Menu, 
  X, 
  LogOut,
  User,
  Settings,
  ChevronDown,
  Layers,
  Hammer,
  ShoppingCart,
  PackageCheck,
  ClipboardList,
  ShoppingBag,
  Receipt,
  ReceiptText,
  FileText,
  BarChart3,
  PanelLeftClose,
  PanelLeftOpen,
  PackageOpen,
  PackagePlus,
  Car,
  UserCheck,
  Route,
  MapPin,
  MapPinned,
  Calculator,
  Tags,
  ListOrdered,
  Building2,
  Percent,
  Store,
  Target,
  DollarSign,
  Landmark,
  FileX2,
  ScrollText,
  CalendarDays,
  Wallet,
  CreditCard,
  Repeat,
  Upload,
  Link2,
  ListTree,
  TrendingUp,
  PiggyBank,
  Factory,
  Users,
  ClipboardCheck,
  Timer,
  PartyPopper,
  Scale,
  Mail,
  BookOpen
} from 'lucide-react';
import { clsx } from 'clsx';
import { NotificationBell } from './NotificationBell';
import { GUIDE_PERMISSIONS } from '@pages/userGuide/userGuideUtils';

// Tipos para menu (suporta até 3 níveis)
type IconType = React.ComponentType<{ size?: number; className?: string } | any>;

type BaseMenuItem = {
  label: string;
  icon: IconType;
  key?: string;
  permission?: string | null;
  /** Visível se o usuário tiver QUALQUER uma destas permissões (OR). */
  anyPermission?: string[];
};

type LinkMenuItem = BaseMenuItem & {
  path: string;
};

type SubParentMenuItem = BaseMenuItem & {
  children: LinkMenuItem[];
};

type ParentMenuItem = BaseMenuItem & {
  children: (LinkMenuItem | SubParentMenuItem)[];
};

type MenuItem = LinkMenuItem | ParentMenuItem;

/**
 * Layout principal da aplicação com sidebar e header
 */
export const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { checkPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  useDragScroll();

  // Environment banner
  const environment = getConfig()?.environment;
  const isNonProduction = environment && environment !== 'production';
  const envLabel = environment === 'development' ? 'Desenvolvimento' : environment === 'homologation' ? 'Homologação' : null;
  const envColor = environment === 'development' ? 'bg-blue-600' : environment === 'homologation' ? 'bg-amber-500' : '';

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);


  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro no logout:', error);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Menu items com permissões e possibilidade de submenus
  const menuItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
      permission: null // Disponível para todos os usuários autenticados
    },
    {
      label: 'Suprimentos',
      icon: Truck,
      key: 'suprimentos',
      children: [
        {
          label: 'Produtos',
          icon: Package,
          path: '/products',
          permission: 'products.read'
        },
        {
          label: 'Fornecedores',
          icon: Truck,
          path: '/providers',
          permission: 'providers.read'
        },
        {
          label: 'Produtos Compostos',
          icon: Layers,
          path: '/composite-products',
          permission: 'composite_products.read'
        },
        {
          label: 'Matérias-Primas',
          icon: Hammer,
          path: '/raw-materials',
          permission: 'raw_materials.read'
        },
        {
          label: 'Pedidos de Compra',
          icon: ShoppingCart,
          path: '/purchase-orders',
          permission: 'purchase_orders.read'
        },
        {
          label: 'Recebimento de Materiais',
          icon: PackageCheck,
          path: '/material-receipts',
          permission: 'material_receipts.read'
        },
        {
          label: 'Conferência de Estoque',
          icon: ClipboardList,
          path: '/stock-movements',
          permission: 'stock_movements.read'
        },
        {
          label: 'Notas Fiscais',
          icon: FileText,
          path: '/supply-invoices',
          permission: 'invoices.read'
        }
      ]
    },
    {
      label: 'Produção',
      icon: Factory,
      key: 'producao',
      children: [
        {
          label: 'Produtos a Fabricar',
          icon: ClipboardList,
          path: '/production/pending',
          permission: 'production.read'
        },
        {
          label: 'Ordens de Produção',
          icon: ClipboardCheck,
          path: '/production/orders',
          permission: 'production.read'
        },
        {
          label: 'Apontamentos',
          icon: Timer,
          path: '/production/activity-logs',
          permission: 'production.read'
        },
        {
          label: 'Lista de Materiais',
          icon: ListTree,
          path: '/production/bom',
          permission: 'production.read'
        },
        {
          label: 'Atividades',
          icon: Hammer,
          path: '/production/activities',
          permission: 'production.read'
        },
        {
          label: 'Colaboradores',
          icon: Users,
          path: '/production/employees',
          permission: 'production.read'
        },
        {
          label: 'Relatórios de Produção',
          icon: BarChart3,
          path: '/production/reports',
          permission: 'production.read'
        }
      ]
    },
    {
      label: 'Vendas',
      icon: ShoppingBag,
      key: 'vendas',
      children: [
        {
          label: 'Pedidos de Venda',
          icon: Receipt,
          path: '/sales-orders',
          permission: 'orders.read'
        },
        {
          label: 'Conciliação de Demonstração',
          icon: Scale,
          path: '/demo-reconciliation',
          permission: 'orders.demo_reconciliation'
        },
        {
          label: 'Notas Fiscais de Produtos',
          icon: FileText,
          path: '/invoices',
          permission: 'invoices.read'
        },
        {
          label: 'Notas Fiscais de Serviço',
          icon: ReceiptText,
          path: '/service-invoices',
          permission: 'service_invoices.read'
        }
      ]
    },
    {
      label: 'Financeiro',
      icon: Wallet,
      key: 'financeiro',
      children: [
        {
          label: 'Conta Corrente',
          icon: Landmark,
          path: '/financial/transactions',
          permission: 'financial.transactions.read'
        },
        {
          label: 'Cartões de Crédito',
          icon: CreditCard,
          path: '/financial/credit-cards',
          permission: 'financial.credit_card.read'
        },
        {
          label: 'Lançamentos Recorrentes',
          icon: Repeat,
          path: '/financial/recurring',
          permission: 'financial.recurring.read'
        },
        {
          label: 'Importação de Extrato',
          icon: Upload,
          path: '/financial/imports',
          permission: 'financial.import.execute'
        },
        {
          // Correlações valem para o extrato E para a fatura de cartão — menu
          // próprio, fora da Importação de Extrato.
          label: 'Correlações de Importação',
          icon: Link2,
          path: '/financial/import-correlations',
          permission: 'financial.import.execute'
        },
        {
          label: 'Plano de Contas',
          icon: ListTree,
          path: '/financial/accounting-plans',
          permission: 'financial.accounting_plan.read'
        },
        {
          label: 'Margem de Produtos',
          icon: TrendingUp,
          path: '/financial/product-earn',
          permission: 'financial.product_earn.read'
        }
      ]
    },
    {
      label: 'Relatórios',
      icon: BarChart3,
      key: 'relatorios',
      children: [
        {
          label: 'Relatório de Vendas',
          icon: BarChart3,
          path: '/reports/sales',
          permission: 'reports.sales'
        },
        {
          label: 'Transporte',
          icon: Truck,
          path: '/reports/shipping',
          permission: 'reports.shipping_pending_delivery'
        },
        {
          label: 'Estoque',
          icon: Package,
          path: '/reports/stock',
          permission: 'reports.stock_movements'
        },
        {
          label: 'Contábil',
          icon: Calculator,
          path: '/reports/accounting',
          permission: 'reports.accounting'
        },
        {
          label: 'Financeiro',
          icon: PiggyBank,
          path: '/reports/financial',
          anyPermission: [
            'reports.financial_revenue',
            'reports.financial_expenses',
            'reports.financial_dre',
            'reports.financial_cashflow',
            'reports.financial_suppliers',
            'reports.financial_payment_methods'
          ]
        },
        {
          label: 'Compras',
          icon: Package,
          path: '/reports/purchases',
          permission: 'reports.purchases'
        }
      ]
    },
    {
      label: 'Configuração',
      icon: Settings,
      key: 'configuracao',
      children: [
        {
          label: 'Fiscal',
          icon: Calculator,
          key: 'configuracao-fiscal',
          children: [
            {
              label: 'Natureza da Operação',
              icon: FileText,
              path: '/settings/fiscal/invoice-transaction-types',
              permission: 'invoice_transaction_types.read'
            },
            {
              label: 'Empresa',
              icon: Building2,
              path: '/settings/fiscal/companies',
              permission: 'companies.read'
            },
            {
              label: 'Invalidação de Notas',
              icon: FileX2,
              path: '/settings/fiscal/invoice-invalidations',
              permission: 'invoice_invalidation.read'
            },
            {
              label: 'Comunicação SEFAZ',
              icon: ScrollText,
              path: '/settings/fiscal/sefaz-communication-logs',
              permission: 'sefaz_communication_log.read'
            }
          ]
        },
        {
          label: 'Geral',
          icon: Settings,
          key: 'configuracao-geral',
          children: [
            {
              label: 'Feriados',
              icon: CalendarDays,
              path: '/settings/general/holidays',
              permission: 'holidays.read'
            }
          ]
        },
        {
          label: 'Operações',
          icon: Settings,
          key: 'configuracao-operacoes',
          children: [
            {
              label: 'Execução de Processos',
              icon: ListOrdered,
              path: '/settings/operations/processes',
              permission: 'processes.read'
            }
          ]
        },
        {
          label: 'Vendas',
          icon: ShoppingBag,
          key: 'configuracao-vendas',
          children: [
            {
              label: 'Tipo de Pedido',
              icon: Tags,
              path: '/settings/sales/order-types',
              permission: 'order_types.read'
            },
            {
              label: 'Status do Pedido',
              icon: ListOrdered,
              path: '/settings/sales/order-statuses',
              permission: 'order_types.read'
            },
            {
              label: 'Canal de Venda',
              icon: Store,
              path: '/settings/sales/sales-channels',
              permission: 'sales_channels.read'
            },
            {
              label: 'Meta de Venda',
              icon: Target,
              path: '/settings/sales/sales-goals',
              permission: 'sales_goals.read'
            }
          ]
        },
        {
          label: 'Transporte',
          icon: Truck,
          key: 'configuracao-transporte',
          children: [
            {
              label: 'Motoristas',
              icon: UserCheck,
              path: '/settings/transport/drivers',
              permission: 'drivers.read'
            },
            {
              label: 'Veículos',
              icon: Car,
              path: '/settings/transport/vehicles',
              permission: 'vehicles.read'
            },
            {
              label: 'Rastreadores',
              icon: Radio,
              path: '/settings/transport/trackers',
              permission: 'fleet_tracking.manage'
            },
            {
              label: 'Transportadoras',
              icon: Truck,
              path: '/settings/transport/shipping-providers',
              permission: 'shipping_providers.read'
            }
          ]
        },
        {
          label: 'Financeiro',
          icon: DollarSign,
          key: 'configuracao-financeiro',
          children: [
            {
              label: 'Custos Variáveis',
              icon: Percent,
              path: '/settings/financial/variable-fees',
              permission: 'variable_fees.read'
            },
            {
              label: 'Hub de Recebimentos',
              icon: Landmark,
              path: '/settings/financial/receiving-hub',
              permission: 'receiving_hub.read'
            },
            {
              label: 'Conta Corrente',
              icon: Landmark,
              path: '/settings/financial/checking-accounts',
              permission: 'financial.accounts.read'
            },
            {
              label: 'Cartões de Crédito',
              icon: CreditCard,
              path: '/settings/financial/credit-cards',
              permission: 'financial.credit_card.read'
            }
          ]
        }
      ]
    },
    {
      label: 'Transporte',
      icon: Truck,
      key: 'transporte',
      children: [
        {
          label: 'Pedidos de Envio',
          icon: Truck,
          path: '/shipping-orders',
          permission: 'invoice_shipping_orders.read'
        },
        {
          label: 'Ordem de Retirada',
          icon: PackageOpen,
          path: '/goods-pickup-orders',
          permission: 'goods_pickup_orders.read'
        },
        {
          label: 'Ordem de Entrega',
          icon: PackagePlus,
          path: '/goods-delivery-orders',
          permission: 'goods_delivery_orders.read'
        },
        {
          label: 'Gerenciar Rotas',
          icon: Route,
          path: '/driver-routes',
          permission: 'driver_routes.read'
        },
        {
          label: 'Entregas',
          icon: MapPin,
          path: '/delivery',
          permission: 'driver_routes.read'
        },
        {
          label: 'Mapa da Frota',
          icon: MapPinned,
          path: '/fleet-map',
          permission: 'fleet_tracking.read'
        }
      ]
    },
    {
      label: 'Eventos',
      icon: PartyPopper,
      key: 'eventos',
      children: [
        {
          label: 'Eventos',
          icon: PartyPopper,
          path: '/events',
          permission: 'events.read'
        },
        {
          label: 'Convites',
          icon: Mail,
          path: '/events/invitations',
          permission: 'events.read'
        },
        {
          label: 'Planejamento de Rota',
          icon: Route,
          path: '/events/route-planning',
          permission: 'events.read'
        }
      ]
    },
    {
      // Manual do usuário: cada perfil vê o seu; o admin vê todos (ERPApi/docs/funcionalidades/manual-do-usuario.md)
      label: 'Documentação',
      icon: BookOpen,
      path: '/documentation',
      anyPermission: GUIDE_PERMISSIONS
    }
  ];

  const isActivePath = (path?: string) => {
    if (!path) return false;
    return location.pathname.startsWith(path);
  };

  const isChildActiveDeep = (child: LinkMenuItem | SubParentMenuItem): boolean => {
    if ('path' in child) return isActivePath(child.path);
    if ('children' in child) return child.children.some((c) => isActivePath(c.path));
    return false;
  };

  const hasMenuPermission = (item: Pick<BaseMenuItem, 'permission' | 'anyPermission'>) => {
    if (item.anyPermission && item.anyPermission.length > 0) {
      return item.anyPermission.some((p) => checkPermission(p).hasPermission);
    }
    if (!item.permission) return true;
    return checkPermission(item.permission).hasPermission;
  };

  const visibleMenuItems: MenuItem[] = menuItems
    .map((item) => {
      if (!hasMenuPermission(item)) return null;

      if (!('children' in item)) return item;

      const visibleChildren = item.children
        .map((child) => {
          if (!hasMenuPermission(child)) return null;

          if (!('children' in child)) return child;

          const visibleSubChildren = child.children.filter((subChild) => hasMenuPermission(subChild));
          if (visibleSubChildren.length === 0) return null;

          return {
            ...child,
            children: visibleSubChildren,
          };
        })
        .filter(Boolean) as (LinkMenuItem | SubParentMenuItem)[];

      if (visibleChildren.length === 0) return null;

      return {
        ...item,
        children: visibleChildren,
      };
    })
    .filter(Boolean) as MenuItem[];

  const renderMenuItem = (item: MenuItem) => {
    if ('children' in item) {
      const key = item.key || item.label;
      const isOpen = !!openMenus[key];
      const isChildActive = item.children.some((c) => isChildActiveDeep(c));

      if (sidebarCollapsed) {
        // Collapsed: show only the parent icon; on click, expand sidebar
        return (
          <div key={key} className="relative group">
            <button
              onClick={() => setSidebarCollapsed(false)}
              className={clsx(
                'flex items-center justify-center w-full p-3 rounded-lg transition-colors',
                isChildActive
                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700'
              )}
              title={item.label}
            >
              <item.icon size={20} />
            </button>
            <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity">
              {item.label}
            </div>
          </div>
        );
      }

      return (
        <div key={key}>
          <button
            onClick={() => setOpenMenus(prev => {
              const willOpen = !prev[key];
              // Accordion: close all other menus when opening one
              if (willOpen) {
                return { [key]: true };
              }
              return { ...prev, [key]: false };
            })}
            className={clsx(
              'flex items-center justify-between w-full gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors',
              isChildActive
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                : 'text-slate-300 hover:text-white hover:bg-slate-700'
            )}
          >
            <div className="flex items-center gap-3">
              <item.icon size={20} />
              {item.label}
            </div>
            <ChevronDown size={16} className={clsx(isOpen ? 'transform rotate-180' : '')} />
          </button>

          {isOpen && (
            <div className="mt-1 space-y-1">
              {item.children.map((child) => {
                // 3rd level: SubParentMenuItem
                if ('children' in child) {
                  const subKey = child.key || child.label;
                  const isSubOpen = !!openMenus[subKey];
                  const isSubActive = child.children.some((sc) => isActivePath(sc.path));

                  return (
                    <div key={subKey}>
                      <button
                        onClick={() => setOpenMenus(prev => ({ ...prev, [subKey]: !prev[subKey] }))}
                        className={clsx(
                          'flex items-center justify-between w-full gap-2 pl-8 pr-4 py-2 text-sm font-medium rounded-lg transition-colors',
                          isSubActive
                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                            : 'text-slate-300 hover:text-white hover:bg-slate-700'
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <child.icon size={16} />
                          {child.label}
                        </div>
                        <ChevronDown size={14} className={clsx(isSubOpen ? 'transform rotate-180' : '')} />
                      </button>
                      {isSubOpen && (
                        <div className="mt-1 space-y-1">
                          {child.children.map((subChild: LinkMenuItem) => {
                            return (
                              <Link
                                key={subChild.path}
                                to={subChild.path}
                                className={clsx(
                                  'flex items-center gap-3 pl-12 pr-4 py-2 text-sm font-medium rounded-lg transition-colors',
                                  isActivePath(subChild.path)
                                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                    : 'text-slate-300 hover:text-white hover:bg-slate-700'
                                )}
                                onClick={closeSidebar}
                              >
                                <subChild.icon size={16} />
                                {subChild.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // 2nd level: LinkMenuItem
                const linkChild = child as LinkMenuItem;
                return (
                  <Link
                    key={linkChild.path}
                    to={linkChild.path}
                    className={clsx(
                      'flex items-center gap-3 pl-8 pr-4 py-2 text-sm font-medium rounded-lg transition-colors',
                      isActivePath(linkChild.path)
                        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                        : 'text-slate-300 hover:text-white hover:bg-slate-700'
                    )}
                    onClick={closeSidebar}
                  >
                    <linkChild.icon size={18} />
                    {linkChild.label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Link item
    const linkItem = item as LinkMenuItem;

    if (sidebarCollapsed) {
      return (
        <div className="relative group">
          <Link
            key={linkItem.path}
            to={linkItem.path}
            className={clsx(
              'flex items-center justify-center p-3 rounded-lg transition-colors',
              isActivePath(linkItem.path)
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                : 'text-slate-300 hover:text-white hover:bg-slate-700'
            )}
            onClick={closeSidebar}
            title={linkItem.label}
          >
            <linkItem.icon size={20} />
          </Link>
          <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity">
            {linkItem.label}
          </div>
        </div>
      );
    }

    return (
      <Link
        key={linkItem.path}
        to={linkItem.path}
        className={clsx(
          'flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors',
          isActivePath(linkItem.path)
            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
            : 'text-slate-300 hover:text-white hover:bg-slate-700'
        )}
        onClick={closeSidebar}
      >
        <linkItem.icon size={20} />
        {linkItem.label}
      </Link>
    );
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar Mobile Backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        'fixed lg:static inset-y-0 left-0 bg-slate-900 text-white flex flex-col z-50 transform transition-all duration-200 ease-in-out',
        sidebarCollapsed ? 'w-16' : 'w-64',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg flex items-center justify-center flex-shrink-0">
              <LayoutDashboard size={20} />
            </div>
            {!sidebarCollapsed && (
              <div>
                <h1 className="text-xs text-slate-400 font-bold">ERP Viver Organizer</h1>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className={clsx('flex-1 space-y-2 overflow-y-auto', sidebarCollapsed ? 'p-2' : 'p-4')}>
          {visibleMenuItems.map(renderMenuItem)}
        </nav>

        {/* Collapse toggle (desktop only) */}
        <div className="hidden lg:block border-t border-slate-800">
          <button
            onClick={() => setSidebarCollapsed(prev => !prev)}
            className="w-full p-3 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors flex items-center justify-center"
            title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {sidebarCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-t border-slate-800">
          <div className={clsx('flex items-center text-sm', sidebarCollapsed ? 'justify-center' : 'gap-3')}>
            <div className="w-8 h-8 gradient-user-avatar rounded-full flex items-center justify-center flex-shrink-0">
              <User size={16} />
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Environment Banner */}
        {isNonProduction && envLabel && (
          <div className={clsx('text-white text-center text-xs font-semibold py-1 tracking-wide', envColor)}>
            ⚠ Ambiente de {envLabel}
          </div>
        )}

        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Mobile menu button */}
            <div className="flex items-center gap-4">
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              
              {/* Page title / breadcrumb */}
              <div className="hidden sm:block">
                <h2 className="text-xl font-semibold text-slate-900">
                  {location.pathname === '/dashboard' && 'Dashboard'}
                  {location.pathname.startsWith('/products') && 'Produtos'}
                  {location.pathname.startsWith('/providers') && 'Fornecedores'}
                  {location.pathname.startsWith('/composite-products') && 'Produtos Compostos'}
                  {location.pathname.startsWith('/raw-materials') && 'Matérias-Primas'}
                  {location.pathname.startsWith('/purchase-orders') && 'Pedidos de Compra'}
                  {location.pathname.startsWith('/material-receipts') && 'Recebimento de Materiais'}
                  {location.pathname.startsWith('/stock-movements') && 'Conferência de Estoque'}
                  {location.pathname.startsWith('/production') && 'Produção'}
                  {location.pathname.startsWith('/sales-orders') && 'Pedidos de Venda'}
                  {location.pathname.startsWith('/demo-reconciliation') && 'Conciliação de Demonstração'}
                  {location.pathname.startsWith('/invoices') && 'Notas Fiscais — Vendas'}
                  {location.pathname.startsWith('/service-invoices') && 'Notas Fiscais de Serviço'}
                  {location.pathname.startsWith('/supply-invoices') && 'Notas Fiscais — Suprimentos'}
                  {location.pathname.startsWith('/financial') && 'Financeiro'}
                  {location.pathname.startsWith('/reports/financial') && 'Financeiro'}
                  {location.pathname.startsWith('/settings/fiscal') && 'Configuração Fiscal'}
                  {location.pathname.startsWith('/settings/financial') && 'Configuração Financeiro'}
                  {location.pathname.startsWith('/settings/transport') && 'Configuração Transporte'}
                  {location.pathname.startsWith('/goods-pickup-orders') && 'Ordens de Retirada'}
                  {location.pathname.startsWith('/goods-delivery-orders') && 'Ordens de Entrega'}
                  {location.pathname.startsWith('/driver-routes') && 'Gerenciar Rotas'}
                  {location.pathname.startsWith('/delivery') && 'Entregas'}
                  {location.pathname.startsWith('/fleet-map') && 'Mapa da Frota'}
                  {location.pathname.startsWith('/events/invitations') && 'Convites'}
                  {location.pathname.startsWith('/events/route-planning') && 'Planejamento de Rota'}
                  {location.pathname.startsWith('/events') && !location.pathname.startsWith('/events/invitations') && !location.pathname.startsWith('/events/route-planning') && 'Eventos'}
                </h2>
              </div>
            </div>

            {/* Header actions */}
            <div className="flex items-center gap-3">
              {/* Notifications: sino generalizado (retiradas pendentes,
                  pedidos prioritários hoje/amanhã e recebimentos de produto
                  personalizado) — ver components/NotificationBell.tsx */}
              <NotificationBell />

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 gradient-user-avatar rounded-full flex items-center justify-center">
                    <User size={16} className="text-white" />
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">
                    {user?.name}
                  </span>
                  <ChevronDown size={16} />
                </button>

                {/* User dropdown */}
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                    <div className="px-3 py-2 border-b border-slate-200">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                      <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                    </div>
                    
                    <button className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                      <User size={14} />
                      Meu Perfil
                    </button>
                    
                    <button className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                      <Settings size={14} />
                      Configurações
                    </button>
                    
                    <div className="border-t border-slate-200 mt-1 pt-1">
                      <button 
                        onClick={handleLogout}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <LogOut size={14} />
                        Sair
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Click outside to close user menu */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </div>
  );
};